import { useState } from 'react'
import { Button, Input, SectionLabel } from '../../../design/components'
import { pageTypeLabel, type ComicPage } from '../types'
import type { PromptBlock, ReferenceImage } from './ComicPageRow'
import styles from './comics.module.css'

/**
 * The prompt for a page's art, assembled from ordered blocks.
 *
 * The API takes a **list of blocks** rather than one string, and the ordering is
 * meaningful: text and reference images interleave in the order the artist would describe
 * them. So this composes a list rather than offering a single box.
 *
 * **Reference images are chosen by id, never uploaded here.** An image block names an
 * existing gallery image, which means a reference is subject to exactly the access check
 * reading it would be — a caller cannot smuggle in a picture they could not otherwise see.
 * What this offers is the images **this comic already holds**, because those are in the
 * sheet already: choosing one costs no extra read and needs no second access decision.
 * Character portraits and location art are equally valid references to the API and would
 * make a good addition; they need a gallery-per-character read this screen does not do.
 *
 * The page's own kind is *not* sent. The server reads it from the page — front covers get
 * the case title, back covers the player-character list — which is why there is no kind
 * picker here even though the request would look natural with one.
 */
export function PagePromptComposer({
  page,
  references,
  onCancel,
  onGenerate,
}: {
  page: ComicPage
  references: ReferenceImage[]
  onCancel: () => void
  onGenerate: (request: { blocks: PromptBlock[]; title?: string }) => void
}) {
  // The script is the obvious first draft of the prompt, so it seeds the first block
  // rather than making the user retype what they already wrote a moment ago.
  const [text, setText] = useState(page.script)
  const [title, setTitle] = useState('')
  const [selected, setSelected] = useState<number[]>([])

  const toggle = (id: number) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    )

  const blocks = buildBlocks(text, selected)

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        onGenerate({ blocks, ...(title.trim() === '' ? {} : { title }) })
      }}
    >
      <SectionLabel tone="ember" micro>
        Draw {pageTypeLabel(page.type).toLowerCase()}
      </SectionLabel>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          What is on the page
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={text}
          autoFocus
          maxLength={4000}
          onChange={(event) => setText(event.target.value)}
        />
      </label>

      {references.length > 0 && (
        <div className={styles.field}>
          <SectionLabel tone="muted" micro>
            Draw from
          </SectionLabel>
          <p className={styles.note}>
            Art already in this comic, to keep faces and places consistent between pages.
          </p>
          <div className={styles.referenceGrid}>
            {references.map((reference) => (
              <button
                key={reference.id}
                type="button"
                className={styles.referenceOption}
                aria-pressed={selected.includes(reference.id)}
                aria-label={reference.label}
                onClick={() => toggle(reference.id)}
              >
                <img className={styles.referenceThumb} src={reference.url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      )}

      <Input
        label="Title"
        value={title}
        hint="Optional — names the image in the page's art."
        onChange={(event) => setTitle(event.target.value)}
      />

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={blocks.length === 0}>
          Generate
        </Button>
      </div>
    </form>
  )
}

/**
 * The blocks the API will receive: the description first, then each reference in the order
 * it was picked.
 *
 * `order` is sent explicitly rather than left to array position — the contract has the
 * field, and a request whose meaning survives being reserialized is worth the two lines.
 */
export function buildBlocks(text: string, referenceIds: readonly number[]): PromptBlock[] {
  const blocks: PromptBlock[] = []

  if (text.trim() !== '') {
    blocks.push({ kind: 'Text', order: 0, text })
  }

  for (const id of referenceIds) {
    blocks.push({ kind: 'Image', order: blocks.length, comicImageId: id })
  }

  return blocks
}
